import { type Accessor, createSignal } from "solid-js";
import { config } from "~/config";
import { downloadBlob } from "~/util/download";
import { combineURL, getAccentColor, slug } from "~/util/helpers";

type PdfGenerator<T> = [Accessor<boolean>, (params: T, element: Element) => Promise<boolean>];

/**
 * Gives an active state and a function to download a pdf from platform
 * @param generator The generator to use in an Accessor for reactive updates
 * @param pdfName The name of the pdf to download in an Accessor for reactive updates
 */
export function createPdfGenerator<T = any>(
	generator: Accessor<string>,
	pdfName: () => string | Promise<string>,
): PdfGenerator<T> {
	const [active, setActive] = createSignal(false);

	async function downloadToolResult(
		generator: string,
		pdfName: string | Promise<string>,
		params: any,
		element: Element,
	): Promise<boolean> {
		const platformUrl = config.platform?.endpoint;

		if (!platformUrl || !params) {
			return false;
		}

		setActive(true);

		try {
			const downloadUrl = combineURL(platformUrl, `generate/${generator}`);
			const response = await fetch(downloadUrl, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					...params,
					meta: {
						accentColor: getAccentColor(element),
					},
				}),
			});

			if (response.status !== 200) {
				const responseText = await response.text();

				console.error(`Failed to generate pdf: ${responseText}`);
				return false;
			}

			let name = pdfName instanceof Promise ? await pdfName : pdfName;
			name = slug(name.replace(".", "_"));

			downloadBlob(await response.blob(), `${name}.pdf`);

			return true;
		} finally {
			setActive(false);
		}
	}

	if (pdfName.constructor.name === "AsyncFunction") {
		return [active, async (params, element) => downloadToolResult(generator(), await pdfName(), params, element)];
	}

	return [active, (params, element) => downloadToolResult(generator(), pdfName() as string, params, element)];
}
